import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { Observable, Subject, interval, takeUntil } from 'rxjs';
import { FunctionAppVersionCheckService } from '../services/function-app-version-check.service';

const VERSION_CHECK_INTERVAL_MS = 10000;

/**
 * Parent layout for every /focus/campaign page (listing, create/edit, opt-out management).
 * Runs the shared Function App version check on entry and every 10 seconds while open, and shows
 * the "Campaigns are temporarily unavailable" overlay on mismatch (US 66801).
 */
@Component({
  selector: 'app-campaign-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './campaign-shell.component.html',
  styleUrls: ['./campaign-shell.component.css'],
})
export class CampaignShellComponent implements OnInit, OnDestroy {
  isMismatch$: Observable<boolean>;
  isChecking$: Observable<boolean>;
  private readonly destroy$ = new Subject<void>();
  /* Lock page scroll for as long as the overlay element exists so the content behind it cannot be scrolled. */
  @ViewChild('overlay') set overlay(el: ElementRef<HTMLElement> | undefined) {
    this.setScrollLocked(!!el);
  }
  private readonly lockedElements = new Map<HTMLElement, string>();

  constructor(
    private versionCheck: FunctionAppVersionCheckService,
    @Inject(DOCUMENT) private document: Document,
    private host: ElementRef<HTMLElement>,
  ) {
    this.isMismatch$ = versionCheck.isMismatch$;
    this.isChecking$ = versionCheck.isChecking$;
  }

  ngOnInit(): void {
    this.versionCheck.check();
    interval(VERSION_CHECK_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.versionCheck.check());
  }

  checkAgain(): void {
    this.versionCheck.check();
  }

  /* The page can scroll on <html>, <body> or an app container above this component, so lock every scrollable ancestor. */
  private setScrollLocked(locked: boolean): void {
    if (!locked) {
      this.lockedElements.forEach((previous, el) => (el.style.overflow = previous));
      this.lockedElements.clear();
      return;
    }
    const view = this.document.defaultView;
    const candidates: HTMLElement[] = [this.document.documentElement, this.document.body];
    for (let el = this.host.nativeElement.parentElement; el; el = el.parentElement) {
      candidates.push(el);
    }
    candidates.forEach(el => {
      const overflowY = view?.getComputedStyle(el).overflowY;
      const isRoot = el === this.document.documentElement || el === this.document.body;
      const scrolls = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
      if ((isRoot || scrolls) && !this.lockedElements.has(el)) {
        this.lockedElements.set(el, el.style.overflow);
        el.style.overflow = 'hidden';
      }
    });
  }

  ngOnDestroy(): void {
    this.setScrollLocked(false);
    this.destroy$.next();
    this.destroy$.complete();
    this.versionCheck.reset();
  }
}
